import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import UserFoldersServices from "../actions/user-folders-actions/services";
import ShowroomCatalogView from "../components/UserFoldersAdmin/ShowroomCatalogView";
import FolderCatalogView from "../components/UserFoldersAdmin/FolderCatalogView";
import { Spinner } from "../components/Spinner";
import { Empty, Button, Tooltip } from "antd";
import { FilePdfOutlined, LeftOutlined, DownloadOutlined } from "@ant-design/icons";
import logo from "../img/logoWithTextBlue.svg";
import "../components/UserFoldersAdmin/UserFoldersAdmin.scss";

const SharedFolder = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const withPricesParam = query.get("p") === "1";
  
  const [folder, setFolder] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const btn = document.getElementById("backToTop");
      if (btn) {
        if (window.scrollY > 400) btn.classList.add("visible");
        else btn.classList.remove("visible");
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (id) {
      let finalId = id;
      try {
        // Try to decode base64. If it's a valid ID, use it.
        const decoded = atob(id);
        if (decoded && !isNaN(Number(decoded))) {
          finalId = decoded;
        }
      } catch (e) {
        // Not base64 or failed to decode, keep original id
      }

      UserFoldersServices.getOneUserFolderPublic(finalId)
        .then((res: any) => {
          setFolder(res.data);
          setLoading(false);
        })
        .catch((err: any) => {
          console.error("Error fetching shared folder:", err);
          setLoading(false);
        });
    }
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="shared_folder_loading">
        <div className="loader_content">
          <Spinner />
          <p>Curating your collection...</p>
        </div>
      </div>
    );
  }

  if (!folder) {
    return (
      <div className="shared_folder_error">
        <Empty 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div className="error_message">
              <h3>Collection Unavailable</h3>
              <p>This collection might have been moved or is no longer public.</p>
              <Button type="primary" onClick={() => window.location.href = "/"}>Return to Site</Button>
            </div>
          } 
        />
      </div>
    );
  }

  return (
    <div className="shared_folder_page viewed_folder_mode shared_folder_active">
      <nav className="shared_nav_glass">
        <div className="nav_container">
          <img src={logo} alt="DEMCO" className="nav_logo" />
          <div className="nav_actions">
            <Tooltip title="Download as PDF">
              <Button 
                type="primary" 
                icon={<DownloadOutlined />} 
                onClick={handlePrint}
                className="pdf_download_btn"
              >
                Download PDF
              </Button>
            </Tooltip>
          </div>
        </div>
      </nav>

      <header className="shared_hero">
        <div className="hero_content">
          <span className="hero_subtitle">CURATED COLLECTION</span>
          <h1 className="hero_title">{folder.name}</h1>
          <div className="hero_divider"></div>
          {folder.description && <p className="hero_description">{folder.description}</p>}
        </div>
      </header>

      <main className="catalog_main">
        <ShowroomCatalogView 
          viewedFolder={folder} 
          withPrices={withPricesParam}
          isAdmin={false} 
          user={null} 
        />
      </main>

      <footer className="shared_footer">
        <div className="footer_logo">
          <img src={logo} alt="DEMCO" />
        </div>
        <p>© {new Date().getFullYear()} DEMCO - ALL RIGHTS RESERVED.</p>
      </footer>

      <Button 
        id="backToTop"
        className="back_to_top"
        icon={<LeftOutlined style={{ transform: 'rotate(90deg)' }} />}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      />

      {/* This is only visible for printing */}
      <FolderCatalogView 
        viewedFolder={folder} 
        withPrices={withPricesParam} 
        isAdmin={false} 
        user={null} 
      />
      
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;800&display=swap');

        .shared_folder_page {
          font-family: 'Outfit', sans-serif;
          background-color: #fbfbfc;
          background-image: 
            radial-gradient(at 0% 0%, rgba(0, 70, 140, 0.03) 0px, transparent 50%),
            radial-gradient(at 100% 100%, rgba(0, 70, 140, 0.03) 0px, transparent 50%);
          min-height: 100vh;
        }

        .back_to_top {
          position: fixed;
          bottom: 40px;
          right: 40px;
          width: 50px;
          height: 50px;
          border-radius: 25px;
          z-index: 1000;
          background: #00468c;
          color: white;
          border: none;
          box-shadow: 0 10px 20px rgba(0, 70, 140, 0.3);
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          visibility: hidden;
          
          &.visible {
            opacity: 1;
            visibility: visible;
          }

          &:hover {
            transform: translateY(-5px) scale(1.1);
            background: #003366;
          }
        }

        .shared_folder_loading {
          height: 100vh;
          display: flex;
          justify-content: center;
          align-items: center;
          background: #fff;
          
          .loader_content {
            text-align: center;
            p {
              margin-top: 20px;
              color: #00468c;
              font-weight: 600;
              letter-spacing: 1px;
              animation: pulse 2s infinite;
            }
          }
        }

        @keyframes pulse {
          0% { opacity: 0.5; }
          50% { opacity: 1; }
          100% { opacity: 0.5; }
        }

        .shared_nav_glass {
          position: sticky;
          top: 20px;
          z-index: 1000;
          margin: 0 40px;
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          border-radius: 20px;
          padding: 15px 30px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
        }

        .nav_container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          max-width: 1400px;
          margin: 0 auto;
        }

        .nav_logo {
          height: 40px;
          transition: transform 0.3s ease;
          &:hover { transform: scale(1.05); }
        }

        .pdf_download_btn {
          background: linear-gradient(135deg, #00468c 0%, #0066cc 100%);
          border: none;
          height: 45px;
          padding: 0 25px;
          border-radius: 12px;
          font-weight: 700;
          letter-spacing: 0.5px;
          box-shadow: 0 5px 15px rgba(0, 70, 140, 0.2);
          
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(0, 70, 140, 0.3);
            background: linear-gradient(135deg, #003366 0%, #00468c 100%);
          }
        }

        .shared_hero {
          padding: 120px 40px 80px;
          text-align: center;
          max-width: 1000px;
          margin: 0 auto;

          .hero_subtitle {
            font-size: 14px;
            font-weight: 800;
            color: #00468c;
            letter-spacing: 5px;
            text-transform: uppercase;
            display: block;
            margin-bottom: 24px;
          }

          .hero_title {
            font-size: clamp(40px, 8vw, 84px);
            font-weight: 800;
            color: #1a1a1a;
            margin: 0;
            line-height: 1;
            letter-spacing: -3px;
            background: linear-gradient(to bottom, #1a1a1a, #475569);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }

          .hero_divider {
            width: 100px;
            height: 6px;
            background: #00468c;
            margin: 40px auto;
            border-radius: 3px;
          }

          .hero_description {
            font-size: 22px;
            color: #64748b;
            line-height: 1.6;
            margin: 0 auto;
            max-width: 700px;
          }
        }

        .catalog_main {
          max-width: 1400px;
          margin: 0 auto 100px;
          padding: 0 40px;
        }

        .shared_footer {
          text-align: center;
          padding: 30px 40px;
          border-top: 1px solid #eee;
          background: white;
          
          .footer_logo img {
            height: 40px;
            margin-bottom: 30px;
            opacity: 0.6;
          }

          p {
            font-size: 13px;
            font-weight: 700;
            color: #94a3b8;
            letter-spacing: 2px;
            margin-bottom: 15px;
          }

          .footer_tagline {
            font-size: 11px;
            font-weight: 800;
            color: #cbd5e1;
            letter-spacing: 4px;
            text-transform: uppercase;
          }
        }

        .error_message {
          h3 { font-size: 24px; font-weight: 800; color: #1e293b; margin-top: 20px; }
          p { color: #64748b; font-size: 16px; margin-bottom: 24px; }
        }

        @media print {
          .shared_nav_glass, .shared_footer, .back_to_top { display: none !important; }
          .shared_hero { padding: 40px 0; }
          .shared_folder_page { background: #fff; }
          .catalog_main { padding: 0; margin-bottom: 0; max-width: none; }
          .hero_title { -webkit-text-fill-color: initial !important; background: none !important; }
        }

        @media (max-width: 768px) {
          .shared_nav_glass { margin: 10px; padding: 10px 20px; }
          .shared_hero { padding: 80px 20px 40px; }
          .catalog_main { padding: 0 15px; }
          .hero_title { font-size: 48px; }
          .back_to_top { bottom: 20px; right: 20px; }
        }
      `}</style>
    </div>
  );
};

export default SharedFolder;
